/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Button, Flex } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { StyledErrorText } from "~/components/Contract/ContractDurationDates";
import { ClearButton } from "~/components/InlineEdit/ClearButton";
import { EditButton } from "~/components/InlineEdit/EditButton";
import { Loading } from "~/components/Utils/Loading";
import { useClickOutside } from "~/tools";

type Props = {
  onSubmit: () => Promise<void>;
  children: React.ReactNode; // Component shown in edit mode
  display: React.ReactNode; // Component shown in display mode
  append?: React.ReactNode; // Component added after edit button
  canEdit?: boolean;
  saveDisabled?: boolean;
  error?: string;
  onEdit?: () => void;
  onClear?: () => Promise<void>;
  editModeOnClear?: boolean;
  width?: string;
};

function InlineEditForm({
  onSubmit,
  children,
  display,
  append,
  canEdit,
  saveDisabled,
  error,
  onEdit,
  onClear,
  editModeOnClear = false,
  width = "100%",
}: Props) {
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [clearLoading, setClearLoading] = React.useState(false);

  const handleSubmit = async () => {
    setIsEditMode(false);
    setIsLoading(true);
    await onSubmit();
    setIsLoading(false);
  };

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsEditMode(false);
    },
    isEditMode
  );

  const edit = () => {
    setIsEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  const clear = async () => {
    if (onClear) {
      setClearLoading(true);
      await onClear();
      setClearLoading(false);
    }
    if (editModeOnClear) {
      setIsEditMode(true);
    }
  };

  if (canEdit === false) {
    return <>{display}</>;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isEditMode) {
    return (
      <form
        onSubmit={handleSubmit}
        css={{ display: "block", width }}
        ref={wrapperRef}
      >
        <Flex>
          {children}

          <Button
            ml=".5rem"
            type="submit"
            icon="save"
            size="md"
            severity="low"
            disabled={saveDisabled || !!error}
          >
            Save
          </Button>

          <Button
            ml=".5rem"
            icon="close"
            size="md"
            severity="low"
            onClick={() => {
              setIsEditMode(false);
            }}
          >
            Cancel
          </Button>
        </Flex>

        {error && <StyledErrorText>{error}</StyledErrorText>}
      </form>
    );
  }

  return (
    <FlexContainer align="center">
      {display}
      <EditButton onClick={edit} />
      {onClear && (
        <ClearButton onClick={clear} isLoading={clearLoading} margin="0" />
      )}
      {append}
    </FlexContainer>
  );
}

export { InlineEditForm };
